import {
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  DestroyRef,
  Directive,
  ElementRef,
  Input,
  QueryList,
  TemplateRef,
  TrackByFunction,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { CdkTableModule } from '@angular/cdk/table';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';

export interface TableColumn {
  key: string;
  label: string;
  /** Autorise le tri sur cette colonne (défaut : true). Mettre à false pour les colonnes d'actions. */
  sortable?: boolean;
  /** Largeur fixe de la colonne (ex. '25%', '120px'). Nécessite un layout fixe (equalColumns). */
  width?: string;
  /** Passe `label` dans le pipe `translate` (défaut : true). À false pour un en-tête déjà composé (unité, devise). */
  translate?: boolean;
}

@Directive({ selector: '[tableCell]', standalone: true })
export class TableCellDirective {
  @Input('tableCell') columnKey!: string;
  constructor(readonly templateRef: TemplateRef<{ $implicit: any }>) {}
}

/** En-tête personnalisé d'une colonne : `<ng-template tableHeader="key" let-column>`. */
@Directive({ selector: '[tableHeader]', standalone: true })
export class TableHeaderDirective {
  @Input('tableHeader') columnKey!: string;
  constructor(readonly templateRef: TemplateRef<{ $implicit: TableColumn }>) {}
}

/** Contenu de la ligne dépliée : `<ng-template tableRowDetail let-row>`. */
@Directive({ selector: '[tableRowDetail]', standalone: true })
export class TableRowDetailDirective {
  constructor(readonly templateRef: TemplateRef<{ $implicit: any }>) {}
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatSortModule,
    CdkTableModule,
    DragDropModule,
    TranslateModule
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements AfterContentInit {
  data = input<any[]>([]);
  columns = input<TableColumn[]>([]);
  searchTerm = input<string | { columnKey: string; searchTerm: string } | undefined>();
  clickable = input<boolean>(false);
  equalColumns = input<boolean>(false);

  rowKey = input<string>('');

  // Highlight / scroll-to
  highlightedRowKey = input<string>('name');
  highlightedRowValue = input<unknown | null>(null);

  // Sort icons (optionnelles)
  sortAscIcon = input<string>('');
  sortDescIcon = input<string>('');
  sortIconFilter = input<string>('');

  // Ligne dépliable (optionnelle) : nécessite un <ng-template tableRowDetail> et rowKey
  expandable = input<boolean>(false);
  expandedRowValue = input<unknown | null>(null);

  // Pagination (optionnelle)
  paginate = input<boolean>(false);
  pageSize = input<number>(10);
  pageSizeOptions = input<number[]>([5, 10, 25, 50, 100]);

  // Output pour émettre la ligne sélectionnée
  selectionLigne = output<any>();

  @ContentChildren(TableCellDirective)
  private _cellTemplates!: QueryList<TableCellDirective>;
  private _cellTemplateMap = new Map<string, TemplateRef<{ $implicit: any }>>();

  @ContentChildren(TableHeaderDirective)
  private _headerTemplates!: QueryList<TableHeaderDirective>;
  private _headerTemplateMap = new Map<string, TemplateRef<{ $implicit: TableColumn }>>();

  @ContentChild(TableRowDetailDirective)
  private _rowDetail?: TableRowDetailDirective;

  /** Colonne unique de la ligne de détail (référence stable pour cdkRowDef). */
  readonly detailColumns = ['__rowDetail__'];
  private readonly _destroyRef = inject(DestroyRef);

  columnOrder = signal<string[]>([]);
  sort = viewChild(MatSort);
  tableContainer = viewChild<ElementRef>('tableContainer');

  currentSort = signal<Sort | null>(null);

  // État de la pagination
  pageIndex = signal<number>(0);
  currentPageSize = signal<number>(10);

  constructor() {
    effect(() => {
      this.columnOrder.set(this.columns().map(col => col.key));
    });

    effect(() => {
      this.currentPageSize.set(this.pageSize());
    });

    afterRenderEffect(() => {
      const value     = this.highlightedRowValue();
      const data      = this.paginatedData();
      const container = this.tableContainer();
      if (!value || !data.length || !container) return;
      this.scrollToHighlighted();
    });
  }

  ngAfterContentInit(): void {
    this._buildTemplateMap();
    this._cellTemplates.changes
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._buildTemplateMap());
    this._headerTemplates.changes
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._buildTemplateMap());
  }

  private _buildTemplateMap(): void {
    this._cellTemplateMap.clear();
    this._cellTemplates.forEach(t => this._cellTemplateMap.set(t.columnKey, t.templateRef));
    this._headerTemplateMap.clear();
    this._headerTemplates.forEach(t => this._headerTemplateMap.set(t.columnKey, t.templateRef));
  }

  getCellTemplate(columnKey: string): TemplateRef<{ $implicit: any }> | null {
    return this._cellTemplateMap.get(columnKey) ?? null;
  }

  getHeaderTemplate(columnKey: string): TemplateRef<{ $implicit: TableColumn }> | null {
    return this._headerTemplateMap.get(columnKey) ?? null;
  }

  get rowDetailTemplate(): TemplateRef<{ $implicit: any }> | null {
    return this._rowDetail?.templateRef ?? null;
  }

  isRowExpanded(row: any): boolean {
    const key = this.rowKey();
    const value = this.expandedRowValue();
    return !!key && value != null && row?.[key] === value;
  }

  displayedColumns = computed(() => this.columnOrder());

  sortedData = computed(() => {
    const sortState = this.currentSort();
    const data = [...this.filteredData()];

    if (!sortState || !sortState.active || sortState.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const aValue = a[sortState.active];
      const bValue = b[sortState.active];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = this.compareValues(aValue, bValue);
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
  });

  paginatedData = computed(() => {
    const data = this.sortedData();

    if (!this.paginate()) {
      return data;
    }

    const startIndex = this.pageIndex() * this.currentPageSize();
    const endIndex = startIndex + this.currentPageSize();
    return data.slice(startIndex, endIndex);
  });

  totalItems = computed(() => this.sortedData().length);

  filteredData = computed(() => {
    const filter = this.searchTerm();
    let allData = [...this.data()];

    if (!filter) return allData;

    if (typeof filter === 'string') {
      const searchValue = filter.trim().toLowerCase();
      if (searchValue !== '') {
        return allData.filter(row =>
          this.columns().some(column => {
            const cellValue = row[column.key];
            return cellValue != null && String(cellValue).toLowerCase().includes(searchValue);
          })
        );
      }
    } else if (filter.columnKey && filter.searchTerm?.trim()) {
      return allData.filter(row => {
        const cellValue = row[filter.columnKey];
        return cellValue != null &&
               String(cellValue).toLowerCase().includes(filter.searchTerm.toLowerCase());
      });
    }

    return allData;
  });

  readonly trackRow: TrackByFunction<any> = (index, row) => {
    const key = this.rowKey();
    const id = key ? row?.[key] : null;
    return id ?? row;
  };

  getCellValue(row: any, columnKey: string): any {
    return row[columnKey] ?? '-';
  }

  isHighlightedRow(row: any): boolean {
    const key = this.highlightedRowKey();
    const value = this.highlightedRowValue();
    return key != null && value != null && row[key] === value;
  }

  onSortChange(sortState: Sort): void {
    this.currentSort.set(sortState);
  }

  private compareValues(a: any, b: any): number {
    const aNum = typeof a === 'number' || !isNaN(Number(a));
    const bNum = typeof b === 'number' || !isNaN(Number(b));
    if (aNum && bNum) return Number(a) - Number(b);
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  }

  drop(event: CdkDragDrop<string[]>): void {
    const columns = [...this.columnOrder()];
    moveItemInArray(columns, event.previousIndex, event.currentIndex);
    this.columnOrder.set(columns);
  }

  onRowClick(row: any): void {
    if (this.clickable()) {
      this.selectionLigne.emit(row);
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.currentPageSize.set(event.pageSize);
  }

  scrollToHighlighted(): void {
    const key   = this.highlightedRowKey();
    const value = this.highlightedRowValue();
    if (!key || value == null) return;

    const container = this.tableContainer()?.nativeElement as HTMLElement | undefined;
    if (!container) return;
    const rowEl = Array.from(container.querySelectorAll<HTMLElement>('tr[data-row-key]'))
      .find(el => el.getAttribute('data-row-key') === String(value));

    if (!rowEl) return;

    const containerRect = container.getBoundingClientRect();
    const rowRect       = rowEl.getBoundingClientRect();
    const rowOffsetTop  = rowRect.top - containerRect.top + container.scrollTop;
    const scrollTop     = rowOffsetTop - container.clientHeight / 2 + rowEl.offsetHeight / 2;

    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }
}
